import React from "react";
import { Helmet } from "react-helmet-async";
import siteData from "../data/siteData";
import { absoluteUrl, normalizePath, truncate } from "../utils/seo";

/**
 * SeoHead — Enhanced SEO + GEO (Generative Engine Optimization) component.
 *
 * Includes:
 * - Standard meta (title, description, canonical)
 * - Open Graph with image dimensions & locale
 * - Twitter Cards with site/creator handles
 * - AI SEO: robots max directives for rich snippets
 * - GEO: author, publisher, article metadata
 * - Structured Data (JSON-LD) injection
 * - Speakable specification hints
 * - noindex/nofollow control
 */
const SeoHead = ({
  title,
  description,
  canonicalPath,
  image,
  imageAlt = siteData.siteName,
  imageWidth,
  imageHeight,
  type = "website",
  noindex = false,
  extraMeta = [],
  jsonLd = [],
  openGraphTitle,
  openGraphDescription,
  twitterCard = "summary_large_image",
  defer = true,
  // GEO / AI SEO props
  author = siteData.siteName,
  datePublished,
  dateModified,
  keywords = [],
  articleSection,
  speakableSelectors = [],
}) => {
  const canonicalUrl = absoluteUrl(normalizePath(canonicalPath));
  const resolvedImage = image ? absoluteUrl(image) : absoluteUrl(siteData.defaultShareImage);
  const resolvedTitle = truncate(title || siteData.siteName, 60);
  const resolvedDescription = truncate(description || siteData.description, 160);

  // AI SEO: robots directive — max previews for rich snippets & AI extraction
  const robotsContent = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  return (
    <Helmet defer={defer}>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* AI SEO: Robots directives for rich snippets */}
      <meta name="robots" content={robotsContent} />

      {/* AI SEO: Author & E-E-A-T signals */}
      <meta name="author" content={author} />

      {/* GEO: Keywords for AI entity recognition */}
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}

      {/* Enhanced Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={openGraphTitle || resolvedTitle} />
      <meta property="og:description" content={openGraphDescription || resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={siteData.siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_IN" />
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}

      {/* Article-specific Open Graph (for blog posts) */}
      {type === "article" && datePublished && (
        <meta property="article:published_time" content={datePublished} />
      )}
      {type === "article" && dateModified && (
        <meta property="article:modified_time" content={dateModified} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}

      {/* Enhanced Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={siteData.socialHandle} />
      <meta name="twitter:creator" content={siteData.socialHandle} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Custom meta tags */}
      {extraMeta.map((meta, index) => (
        <meta key={`${meta.name || meta.property || "meta"}-${index}`} {...meta} />
      ))}

      {/* JSON-LD Structured Data */}
      {jsonLd.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
